import { db } from '../../data/db.ts';
import { makeCloze, type Cloze } from '../../core/cloze.ts';
import { acceptedAnswers } from '../../core/grader.ts';
import {
  isKana,
  isKatakana,
  splitKanjiReading,
  toHiragana,
  toKatakana,
  toRomaji,
} from '../../core/kana.ts';
import { mulberry32, shuffle } from '../../core/rng.ts';
import { cardIdFor } from '../../data/repositories/reviews.ts';
import type { FrequencyBand } from '../../core/frequency.ts';
import type { Item, TargetLang } from '../../data/types.ts';
import { anchorPool, getAnchor, type AnchorSentence } from '../../data/content.ts';
import { glossFor } from '../../data/glosses.ts';
import { selectGraded } from '../../core/coverage.ts';
import { tokenizeLatin } from '../../core/tokenize.ts';
import { presentableLevel, TEXT_ONLY_MAX_LEVEL } from '../../core/ladder.ts';
import { getMnemonic } from '../../data/repositories/mnemonics.ts';
import { kanjiStability } from '../../data/repositories/kanji.ts';
import { baselineMnemonic } from './mnemonic.ts';
import type { LadderLevel } from '../../data/types.ts';

/**
 * Turns an item into something a learner can answer at its current rung.
 *
 * **Deviation from SPEC §2.3, and why.** The ladder specifies L2 as
 * "target → meaning, typed". Grading that needs an Indonesian gloss to compare
 * against, and no gloss source is licence-cleared yet (risk R3). The available
 * alternative — grading a typed translation against the one Indonesian sentence
 * we ship — would mark perfectly good paraphrases wrong, which is exactly the
 * unfairness the grader exists to prevent.
 *
 * So M2 splits contextual production into two rungs by *support* rather than
 * by task: L2 is a cloze with the Indonesian translation visible, L3 the same
 * cloze without it. That is a real difficulty gradient, it uses only data we
 * have, and it reverts to the spec's shape the moment glosses land.
 */

export type TaskKind =
  | 'exposure'
  | 'recognition'
  | 'cloze-supported'
  | 'cloze-unaided'
  | 'dictation'
  | 'kanji'
  | 'kana'
  | 'production'
  | 'free';

export interface Task {
  itemId: string;
  cardId: string;
  headword: string;
  /**
   * The headword's reading, for SPEC §4.3's rungs below kanji. The *display*
   * form is `headwordIn(scriptMode, headword, reading)`; `headword` stays the
   * answer and what a verdict shows, so the two never drift.
   */
  reading?: string;
  ladderLevel: LadderLevel;
  kind: TaskKind;
  sentence: {
    id: string;
    text: string;
    /**
     * Japanese only: build-time tokens and their katakana readings (D10). The
     * app never tokenizes at runtime, so furigana is assembled from these —
     * `furiganaFor` needs both and has nothing to do without them.
     */
    tokens?: readonly string[];
    readings?: readonly string[];
  };
  /**
   * Stability in days per kanji in the sentence, `null` where the learner has
   * no card for it (SPEC §10's per-kanji fade). Resolved at build time because
   * `furiganaFor` is pure and synchronous, and because a card's stability does
   * not change while the card is on screen — but the *script mode* can, so the
   * segments are built in the view rather than here.
   */
  kanjiStability?: Record<string, number | null>;
  /**
   * The kana characters in this sentence the learner can already read
   * (SPEC §4.3). The romaji rung shows a word in kana once every character in
   * it is here, so the script fades in as the syllabary is earned rather than
   * when someone changes a setting.
   */
  readableKana?: string[];
  translation: string;
  /** Recognition only: the correct translation plus distractors, shuffled. */
  options?: string[];
  /**
   * Indonesian senses for the word itself (SPEC §2.3). Absent where the dictionary
   * has nothing — coverage is partial and measured, never invented.
   */
  gloss?: readonly string[];
  /**
   * A chunk's L1 trap, in Indonesian (SPEC §2.5, §2.9). Present only where the
   * phrase has one — "take a shower" does, "have lunch" does not.
   */
  chunkNote?: string;
  /** Cloze rungs only. */
  cloze?: Cloze;
  /** Kanji cards only (SPEC §2.11). */
  kanji?: KanjiFace;
  /** SPEC §4.3's syllabary, as a card. Present only on a `kana` task. */
  kana?: KanaFace;
  /** What a typed answer is graded against, and what the verdict shows. */
  answer: string;
  /**
   * Other forms that answer counts as (SPEC §2.7, §4.3) — in practice the
   * hiragana reading of a kanji headword. `KanaInput` has no kanji conversion
   * step, so for the 71.9% of Japanese lexemes written with kanji the reading
   * is the only form a learner without a system IME can type, and §4.3 has
   * them at `kana` script mode anyway. Built by `acceptedAnswers`.
   */
  alsoAccepted?: readonly string[];
  /** SPEC §2.12: confidence is asked before the reveal on recall rungs. */
  asksConfidence: boolean;
  /**
   * The rung this task actually presents, which is the card's level clamped to
   * `ladderCeiling(hasAudio)`. `recordReview` applies the same clamp, so the
   * review log records the rung the learner really answered at.
   */
  ceiling: LadderLevel;
}

const kindForLevel = (level: LadderLevel): TaskKind => {
  switch (level) {
    case 0:
      return 'exposure';
    case 1:
      return 'recognition';
    case 2:
      return 'cloze-supported';
    case 3:
      return 'cloze-unaided';
    case 4:
      return 'dictation';
    case 5:
      // SPEC §2.3 L5: ID → target, produced. The learner writes the word from
      // the Indonesian alone — no frame, no options, nothing to recognize.
      return 'production';
    default:
      // L6: use it in a sentence about your own life.
      return 'free';
  }
};

/**
 * Longest sentence a learner is asked to write back from hearing it once.
 *
 * Past this, dictation stops measuring phonological form and starts measuring
 * working memory — which is a different construct and not one SPEC §2.3 asked
 * for. An item whose anchors are all longer than this simply has no L4 material,
 * and is held at L3 by the same mechanism that withholds it for missing audio.
 */
export const DICTATION_MAX_TOKENS = 10;

/** What a kanji card shows (SPEC §2.11). */
/** One character of the syllabary, and the sound it makes. */
export interface KanaFace {
  character: string;
  romaji: string;
  script: 'hiragana' | 'katakana';
  /** The other script's form of the same sound — あ for ア, and the reverse. */
  counterpart: string;
}

export interface KanjiFace {
  literal: string;
  /** The component breakdown, e.g. 校 → 木 + 交 (decision D41). */
  components: string[];
  /**
   * What the character itself is read as — KANJIDIC2's notation already
   * unpacked, so never `あ.う` or `ひと-` (see `splitKanjiReading`).
   */
  reading: string | null;
  /**
   * The kana written after it in the word, where the dictionary marks any.
   * Present for 863 of the 1,748 shipped kanji; the card spells out the word
   * they form, because `あ` alone and `あう` alone are each only half true.
   */
  okurigana?: string;
  /**
   * The character's reading in latin letters — what a day-one learner can
   * actually say. Derived from `reading`, so it follows the okurigana split.
   */
  romaji: string | null;
  /** KANJIDIC2's English meanings, labelled as English on the card (D106). */
  meanings: string[];
  /** The learner's own mnemonic if they wrote one, otherwise the baseline. */
  mnemonic: string;
  mnemonicIsMine: boolean;
}

const DISTRACTORS = 3;

/**
 * SPEC §2.3: distractors come from the same frequency band, so a learner
 * cannot pick the answer by noticing that three options are obviously harder.
 */
const pickDistractors = async (
  sentence: AnchorSentence,
  lang: TargetLang,
  band: FrequencyBand,
  seed: number,
): Promise<string[]> => {
  const pool = await anchorPool(lang, band);
  const candidates = pool.filter(
    (candidate) => candidate.tr.id !== sentence.tr.id && candidate.tr.text.length > 0,
  );
  return shuffle(candidates, mulberry32(seed))
    .slice(0, DISTRACTORS)
    .map((candidate) => candidate.tr.text);
};

export interface BuildTaskOptions {
  /** Lexeme ids the learner currently knows, for the i+1 anchor choice (SPEC §2.4). */
  known?: ReadonlySet<string>;
  /**
   * SPEC §2.6: whether this sentence can be presented as audio — a pre-cached
   * clip or a working TTS voice. Defaults to "no", which withholds L4 rather
   * than assuming an engine we have not probed.
   */
  hasAudioFor?: (sentenceId: string) => boolean;
}

/**
 * The kana of a sentence the learner has already learned to read.
 *
 * Over the readings as well as the surface, because a kanji token's kana only
 * exists in its reading — and the romaji rung falls back to that reading when
 * it cannot show the kanji.
 */
const readableKanaIn = (
  sentence: { text: string; tokens?: readonly string[]; readings?: readonly string[] },
  known: ReadonlySet<string>,
): string[] => {
  const source = [sentence.text, ...(sentence.readings ?? [])].join('');
  const characters = new Set(
    [...source].filter((character) => isKana(character)).map(toHiragana),
  );
  return [...characters].filter((character) => known.has(`ja:kana:${character}`));
};

/**
 * A kanji's parts, which do not include the kanji.
 *
 * KRADFILE lists a character among its own radicals. Measured over the shipped
 * shard: **62 of 1,748** have a breakdown that is nothing but the character
 * itself, and **78 more** list it beside real components — so the card said
 * *"日 tersusun dari 日"* and *"見 tersusun dari 見 + 目 + 儿"*, then invited the
 * learner to build a story out of those parts.
 *
 * Dropping the self-reference fixes both at once, and is what finally lets
 * `baselineAtomic` fire: *"日 adalah bentuk dasar"* was written for exactly the
 * first case and could never run, because it is guarded on an empty breakdown
 * and no shipped kanji has one.
 */
const componentsOf = (item: Item): string[] =>
  (item.componentsOf ?? []).filter((part) => part !== item.headword);

/**
 * The reading fields for a kanji face, with KANJIDIC2's notation unpacked.
 *
 * The card printed the raw string: 会 was shown as `あ.う` under the heading
 * "Dibaca", for 878 of the 1,748 shipped kanji. Collapsing the dot would make
 * it false — あう is the reading of 会う, not of 会 — so both halves are kept
 * and the card states each one.
 */
const kanjiReadingOf = (
  raw: string | undefined,
): { reading: string | null; romaji: string | null; okurigana?: string } => {
  if (raw === undefined || raw.length === 0) return { reading: null, romaji: null };
  const { reading, okurigana } = splitKanjiReading(raw);
  // The character's own reading, romanised. Not the whole word: `romaji` sits
  // under `reading`, and the two have to agree.
  return {
    reading,
    romaji: toRomaji(toHiragana(reading)),
    ...(okurigana === null ? {} : { okurigana }),
  };
};

/**
 * The `alsoAccepted` fragment, or nothing at all.
 *
 * Spread rather than returned as a value because `exactOptionalPropertyTypes`
 * is on: an absent field and a field set to `undefined` are different types,
 * and a one-element list (the answer itself) would just be graded twice.
 */
const alsoAcceptedFor = (
  item: Item,
  answer: string,
): { alsoAccepted?: readonly string[] } => {
  const accepted = acceptedAnswers({
    lang: item.lang,
    answer,
    ...(item.reading !== undefined ? { reading: item.reading } : {}),
  });
  return accepted.length > 1 ? { alsoAccepted: accepted.slice(1) } : {};
};

export const buildTask = async (
  profileId: string,
  itemId: string,
  seed: number,
  options: BuildTaskOptions = {},
): Promise<Task | null> => {
  const item = await db.items.get(itemId);
  if (!item) return null;

  const card = await db.cards.get(cardIdFor(profileId, itemId));
  const level = card?.ladderLevel ?? 0;
  const hasAudioFor = options.hasAudioFor ?? (() => false);

  // SPEC §4.3: a character of the syllabary is taught as itself. It has no
  // example sentence and needs none — the sound *is* the content — so like a
  // kanji it never goes down the anchor path below.
  //
  // The rung decides whether the card shows the sound or asks for it; it never
  // climbs past L2, because there is no production task beyond "write the
  // character for this sound" and no listening one without audio.
  if (item.kind === 'kana') {
    const character = item.headword;
    const counterpart = isKatakana(character) ? toHiragana(character) : toKatakana(character);
    return {
      itemId,
      cardId: cardIdFor(profileId, itemId),
      headword: character,
      ladderLevel: Math.min(level, 2) as LadderLevel,
      ceiling: 2 as LadderLevel,
      kind: 'kana',
      sentence: { id: itemId, text: character },
      translation: '',
      answer: item.reading ?? '',
      asksConfidence: false,
      kana: {
        character,
        romaji: item.reading ?? '',
        script: isKatakana(character) ? 'katakana' : 'hiragana',
        counterpart,
      },
    };
  }

  // SPEC §2.11: a kanji is taught by its components and readings, not through an
  // example sentence, so it never goes down the anchor path below — and §2.5's
  // "every lexeme needs a sentence" rule is about lexemes, not characters.
  if (item.kind === 'kanji') {
    const mine = await getMnemonic(profileId, itemId);
    return {
      itemId,
      cardId: cardIdFor(profileId, itemId),
      headword: item.headword,
      ladderLevel: Math.min(level, TEXT_ONLY_MAX_LEVEL) as LadderLevel,
      ceiling: TEXT_ONLY_MAX_LEVEL,
      kind: 'kanji',
      sentence: { id: item.id, text: item.headword },
      translation: '',
      answer: item.headword,
      asksConfidence: false,
      kanji: {
        literal: item.headword,
        components: componentsOf(item),
        ...kanjiReadingOf(item.reading),
        meanings: item.meanings ?? [],
        mnemonic: mine?.text ?? baselineMnemonic(item.headword, componentsOf(item)),
        mnemonicIsMine: mine !== null,
      },
    };
  }

  // Which example sentence teaches this word is an i+1 decision (SPEC §2.4):
  // among the anchors, pick the one whose coverage best fits what this learner
  // already knows, rather than always the globally easiest.
  // A chunk carries its own examples (SPEC §2.5): its anchors are drawn from
  // the whole corpus, while `getAnchor` reads only the band's curated anchor
  // shard (D19), so resolving them by id silently failed for a fifth of them —
  // `buildTask` returned null and the session skipped the item without a trace.
  const anchors =
    item.examples !== undefined && item.examples.length > 0
      ? item.examples
      : (
          await Promise.all(
            item.anchorSentenceIds.map((id) => getAnchor(item.lang, item.band, id)),
          )
        ).filter((anchor): anchor is AnchorSentence => anchor !== null);
  if (anchors.length === 0) return null;

  /**
   * The word's own meaning, resolved once for **every** rung.
   *
   * This used to be fetched only on the `exposure` branch, so L1–L6 carried no
   * gloss at all even where one existed — and L5, whose whole job is
   * "meaning → produce the word" (§2.3), had nothing to show but the sentence
   * translation. `glossFor` caches per band, so asking on every task costs one
   * shard read per band rather than one per card (§5.4).
   *
   * A chunk's authored gloss wins: its parts do not compose, so a per-word
   * lookup cannot stand in for it (§2.5).
   */
  const gloss =
    item.gloss !== undefined ? [item.gloss] : await glossFor(item.lang, item.band, itemId);

  const base = {
    itemId,
    cardId: cardIdFor(profileId, itemId),
    headword: item.headword,
    ...(item.reading !== undefined ? { reading: item.reading } : {}),
    ...(gloss.length > 0 ? { gloss } : {}),
  };

  // L4 first, because it is the only rung with a precondition. A dictation card
  // needs a sentence that can be *heard* and is short enough to write back; if
  // no anchor qualifies, the item is not L4-eligible and its ceiling drops to
  // L3 — SPEC §2.6's exclusion, applied per item rather than app-wide.
  if (level >= 4) {
    // Counted over the anchor's own tokens where it has them (D99). The
    // Latin tokenizer finds no Japanese words, so measured over the shipped
    // band-1 anchors this gate admitted 100% of them rather than 99.9% — it
    // was not wrong often, but it was not measuring anything either.
    const audible = anchors.find(
      (anchor) =>
        hasAudioFor(anchor.id) &&
        (anchor.tokens ?? tokenizeLatin(anchor.text)).length <= DICTATION_MAX_TOKENS,
    );
    if (audible) {
      return {
        ...base,
        ladderLevel: 4,
        ceiling: 4,
        kind: 'dictation',
        sentence: { id: audible.id, text: audible.text },
        translation: audible.tr.text,
        answer: audible.text,
        asksConfidence: true,
      };
    }
  }

  // A dictation card on a silent device is presented (and logged) one rung down.
  const effectiveLevel = presentableLevel(level, false);

  const lapses = card?.fsrs.lapses ?? 0;
  let sentence: AnchorSentence;
  if (lapses > 0) {
    // SPEC §7.2: a leech is re-taught with a *fresh* sentence, never the same
    // one again — so rotation wins over the coverage fit here.
    sentence = anchors[lapses % anchors.length]!;
  } else {
    sentence =
      selectGraded(anchors, options.known ?? new Set(), item.lang)?.item ?? anchors[0]!;
  }

  const cloze = makeCloze(sentence.text, item.headword);
  // A cloze rung with no blank to make is unanswerable — fall back a rung
  // rather than show a sentence with nothing missing.
  const kind = kindForLevel(effectiveLevel);
  const effective: TaskKind =
    (kind === 'cloze-supported' || kind === 'cloze-unaided') && !cloze ? 'recognition' : kind;

  // SPEC §10: *"Furigana auto-fades per-kanji as stability rises."* Both halves
  // ride on the task — the pipeline's tokens and readings, and how well this
  // learner knows each character — because `furiganaFor` is pure and
  // synchronous and this is the last place that can read the database.
  const tokens = sentence.tokens;
  const readings = sentence.readings;
  const japanese = item.lang === 'ja' && tokens !== undefined && readings !== undefined;
  const withSentence = {
    ...base,
    ladderLevel: effectiveLevel,
    ceiling: TEXT_ONLY_MAX_LEVEL,
    sentence: {
      id: sentence.id,
      text: sentence.text,
      ...(japanese ? { tokens, readings } : {}),
    },
    ...(japanese
      ? {
          kanjiStability: await kanjiStability(profileId, sentence.text),
          readableKana: readableKanaIn(sentence, options.known ?? new Set()),
        }
      : {}),
    translation: sentence.tr.text,
  };

  // SPEC §2.3 L5/L6 — the production rungs. Both grade against the headword
  // itself: L5 because that *is* the answer, L6 because the only thing about a
  // free sentence we can honestly check is whether the learner used the word.
  if (effective === 'production' || effective === 'free') {
    return {
      ...withSentence,
      kind: effective,
      answer: item.headword,
      ...alsoAcceptedFor(item, item.headword),
      asksConfidence: effective === 'production',
    };
  }

  if (effective === 'recognition') {
    const distractors = await pickDistractors(sentence, item.lang, item.band, seed);
    return {
      ...withSentence,
      kind: 'recognition',
      options: shuffle([sentence.tr.text, ...distractors], mulberry32(seed + 1)),
      answer: sentence.tr.text,
      asksConfidence: false,
    };
  }

  if (effective === 'exposure') {
    return {
      ...withSentence,
      kind: 'exposure',
      answer: item.headword,
      asksConfidence: false,
      ...(item.chunkNote !== undefined ? { chunkNote: item.chunkNote } : {}),
    };
  }

  const clozeAnswer = cloze?.answer ?? item.headword;
  return {
    ...withSentence,
    kind: effective,
    ...(cloze ? { cloze } : {}),
    answer: clozeAnswer,
    // `makeCloze` slices the matched headword out of the sentence, so a cloze
    // answer *is* the headword and the headword's reading applies to it.
    ...alsoAcceptedFor(item, clozeAnswer),
    asksConfidence: true,
  };
};
